import { DT } from '@fafi/shared';

// Clock sync (§10.4): estimate the server clock from ping/pong RTT. offset = serverTime + rtt/2 −
// clientNow; keep a best-RTT window (least jitter) and slew the exposed offset ≤ 2 ms/s. Exposes
// serverNowMs / currentServerTick / rttMs (feeds the §10.2 input-lead). Pure — a virtual clock drives it.

const TICK_MS = DT * 1000;
const RTT_WINDOW = 8;
const SLEW_MS_PER_MS = 2 / 1000; // ≤ 2 ms per second

export class ClockSync {
  private offsetMs = 0;
  private initialized = false;
  private lastPongClientMs = 0;
  private readonly window: Array<{ rtt: number; offset: number }> = [];
  /** tick↔wall anchor from the pong's serverTick (M2-S9): the server's tick 0 is NOT wall-time 0 in
   *  production (epoch clock), so tick derivation must anchor on a (tick, serverMs) pair. The virtual
   *  harness aligns both domains, which is why the tests alone never exposed this. */
  private anchor: { tick: number; serverMs: number } | null = null;
  rttMs = 0;

  /** D46 Item 6: re-arm for a NEW match epoch. A rematch restarts the server tick timeline at 0; the old
   *  anchor would put currentServerTick() thousands of ticks ahead of the fresh snapshots. Dropping the
   *  anchor forces the next pong's serverTick to re-anchor the tick↔wall mapping cleanly. */
  reset(): void {
    this.offsetMs = 0;
    this.initialized = false;
    this.lastPongClientMs = 0;
    this.window.length = 0;
    this.anchor = null;
    this.rttMs = 0;
  }

  /** Feed a pong: the client time the ping was sent, the server time it reported, and now (client).
   *  `serverTick` (when provided) anchors the tick timeline to the server wall clock. */
  onPong(pingClientMs: number, serverTimeMs: number, nowClientMs: number, serverTick?: number): void {
    if (serverTick !== undefined) this.anchor = { tick: serverTick, serverMs: serverTimeMs };
    const rtt = Math.max(0, nowClientMs - pingClientMs);
    const rawOffset = serverTimeMs + rtt / 2 - nowClientMs;
    this.rttMs = rtt;
    this.window.push({ rtt, offset: rawOffset });
    if (this.window.length > RTT_WINDOW) this.window.shift();

    // target = the offset from the lowest-RTT sample in the window (least queuing jitter)
    let best = this.window[0]!;
    for (const s of this.window) if (s.rtt < best.rtt) best = s;
    const target = best.offset;

    if (!this.initialized) {
      this.offsetMs = target;
      this.initialized = true;
    } else {
      const dt = Math.max(0, nowClientMs - this.lastPongClientMs);
      const maxStep = SLEW_MS_PER_MS * dt;
      const d = target - this.offsetMs;
      this.offsetMs += Math.sign(d) * Math.min(Math.abs(d), maxStep);
    }
    this.lastPongClientMs = nowClientMs;
  }

  serverNowMs(nowClientMs: number): number {
    return nowClientMs + this.offsetMs;
  }
  /** D117: the server tick as a REAL number — the fraction is how far through the current tick we are.
   *  Presentation reads it to render the predicted own entity sub-tick; input stamping keeps using the
   *  floor below, so the wire is untouched. */
  currentServerTickFloat(nowClientMs: number): number {
    if (this.anchor) return this.anchor.tick + (this.serverNowMs(nowClientMs) - this.anchor.serverMs) / TICK_MS;
    return this.serverNowMs(nowClientMs) / TICK_MS; // anchor-less: tick 0 ↔ serverMs 0 (test harness)
  }
  currentServerTick(nowClientMs: number): number {
    return Math.floor(this.currentServerTickFloat(nowClientMs));
  }
  get offset(): number {
    return this.offsetMs;
  }
}
